import React, { useState, useCallback } from 'react'
import { Box, Fade, Tabs, Tab, Tooltip } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import CampaignIcon from '@mui/icons-material/Campaign'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { useTranslation } from 'react-i18next'
import Form from './form'
import TtsPlayground from '../components/tts/TtsPlayground'
import RoundTripDemo from '../components/tts/RoundTripDemo'
import { useTts } from '../contexts/TtsContext'
import { usePageTitle } from '../hooks/usePageMeta'
import { tokens } from '../theme/tokens'

const SpeechPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { ttsHealthy } = useTts()
  usePageTitle('Speech')

  // Determine active tab from URL
  const activeTab = location.pathname.includes('/speech/roundtrip')
    ? 2
    : location.pathname.includes('/speech/tts')
      ? 1
      : 0

  // Lifted TTS state (persists across tab switches)
  const [ttsText, setTtsText] = useState('')
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('')

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 2 && ttsHealthy === false) return
    const routes = ['/speech/asr', '/speech/tts', '/speech/roundtrip']
    navigate(routes[newValue], { replace: true })
  }, [navigate, ttsHealthy])

  const roundTripTab = (
    <Tab
      id="speech-tab-2"
      aria-controls="speech-tabpanel-2"
      icon={<CompareArrowsIcon />}
      iconPosition="start"
      label={t('speech.roundTripTab', 'Round-Trip Demo')}
      disabled={ttsHealthy === false}
    />
  )

  return (
    <Fade in timeout={300}>
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label={t('speech.tabs', 'Speech mode tabs')}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              minHeight: 48,
              borderRadius: 0,
              background: 'none',
              color: 'text.secondary',
              '&.Mui-selected': {
                background: 'none',
                color: activeTab === 1 || activeTab === 2
                  ? tokens.colors.tts.main
                  : tokens.colors.primary.darkText,
              },
            },
            '& .MuiTabs-indicator': {
              display: 'block',
              backgroundColor: activeTab === 1 || activeTab === 2
                ? tokens.colors.tts.main
                : tokens.colors.primary.darkText,
            },
          }}
        >
          <Tab
            id="speech-tab-0"
            aria-controls="speech-tabpanel-0"
            icon={<RecordVoiceOverIcon />}
            iconPosition="start"
            label={t('speech.asrTab', 'ASR - Speech to Text')}
          />
          <Tab
            id="speech-tab-1"
            aria-controls="speech-tabpanel-1"
            icon={<CampaignIcon />}
            iconPosition="start"
            label={t('speech.ttsTab', 'TTS - Text to Speech')}
          />
          {ttsHealthy === false ? (
            <Tooltip title={t('speech.ttsUnavailable', 'TTS service unavailable')}>
              <span>{roundTripTab}</span>
            </Tooltip>
          ) : (
            roundTripTab
          )}
        </Tabs>
      </Box>

      {/* All tab panels kept mounted; visibility toggled via display to preserve state */}
      <Box
        role="tabpanel"
        id="speech-tabpanel-0"
        aria-labelledby="speech-tab-0"
        hidden={activeTab !== 0}
        sx={{ display: activeTab === 0 ? 'block' : 'none' }}
      >
        <Form />
      </Box>
      <Box
        role="tabpanel"
        id="speech-tabpanel-1"
        aria-labelledby="speech-tab-1"
        hidden={activeTab !== 1}
        sx={{ display: activeTab === 1 ? 'block' : 'none' }}
      >
        <TtsPlayground
          text={ttsText}
          setText={setTtsText}
          selectedVoiceId={selectedVoiceId}
          setSelectedVoiceId={setSelectedVoiceId}
        />
      </Box>
      <Box
        role="tabpanel"
        id="speech-tabpanel-2"
        aria-labelledby="speech-tab-2"
        hidden={activeTab !== 2}
        sx={{ display: activeTab === 2 ? 'block' : 'none' }}
      >
        <RoundTripDemo />
      </Box>
    </Box>
    </Fade>
  )
}

export default SpeechPage
